import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Deviation, WaybillIdView } from '../../../models';
import {
  ShipmentWaybillCustomsDocumentsCountLoad,
  ShipmentWaybillItemNotificationListCountLoad,
  ShipmentWaybillItemSetIdentifier,
  ShipmentWaybillItemSetInitial,
  ShipmenWaybillDemurrageAndDetentionTabAvailability,
} from './shipment-waybill-item.actions';
import { Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CustomHttpErrorResponse, Error, NotificationService, Permission, PERMISSION_CONFIG } from '../../../../common';
import { ShipmentWaybillItemService } from './shipment-waybill-item.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { AuthFacade } from '../../auth';

export interface ShipmentWaybillItemStateModel {
  shipmentWaybillIdentifier: WaybillIdView;
  waybillDeviation: Deviation;
  waybillCustomsDocumentsCount: number;
  notificationListCount: number;
  demurrageAndDetentionAvailable: boolean;
  loaded: boolean;
  documentsLoaded: boolean;
  notificationsLoaded: boolean;
  dndLoaded: boolean;
}

const initialState: ShipmentWaybillItemStateModel = {
  shipmentWaybillIdentifier: {
    waybillIdType: null,
    internalWaybillId: null,
    carrierId: null,
    mbl: null,
    carrierTO: null,
    hawb: null,
    hbl: null,
  },
  waybillDeviation: null,
  waybillCustomsDocumentsCount: 0,
  notificationListCount: null,
  demurrageAndDetentionAvailable: false,
  loaded: false,
  documentsLoaded: false,
  notificationsLoaded: false,
  dndLoaded: false,
};

@State<ShipmentWaybillItemStateModel>({
  name: 'shipmentWaybillItem',
  defaults: initialState,
})
@Injectable()
export class ShipmentWaybillItemState {
  constructor(
    private shipmentWaybillItemService: ShipmentWaybillItemService,
    private notificationService: NotificationService,
    private authFacade: AuthFacade,
  ) {}

  @Selector()
  static getState(state: ShipmentWaybillItemStateModel): ShipmentWaybillItemStateModel {
    return state;
  }

  @Selector()
  static getWaybillIdentifier(state: ShipmentWaybillItemStateModel): WaybillIdView {
    return state.shipmentWaybillIdentifier;
  }

  @Selector()
  static getShipmentWaybillCustomsDocumentsCount(state: ShipmentWaybillItemStateModel): number {
    return state.waybillCustomsDocumentsCount;
  }

  @Selector()
  static getShipmentWaybillHasNotification(state: ShipmentWaybillItemStateModel): number {
    return state.notificationListCount;
  }

  @Selector()
  static getDemurrageAndDetentionAvailability(state: ShipmentWaybillItemStateModel): boolean {
    return state.demurrageAndDetentionAvailable;
  }

  @Selector()
  static getLoaded(state: ShipmentWaybillItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getTabsLoaded(state: ShipmentWaybillItemStateModel): boolean {
    return state.documentsLoaded && state.notificationsLoaded && state.dndLoaded;
  }

  @Action(ShipmentWaybillItemSetIdentifier)
  setWaybillIdentifier(
    ctx: StateContext<ShipmentWaybillItemStateModel>,
    { payload }: ShipmentWaybillItemSetIdentifier,
  ): void {
    ctx.patchState({ ...ctx.getState(), shipmentWaybillIdentifier: payload, loaded: true });
  }

  @Action(ShipmentWaybillCustomsDocumentsCountLoad)
  getWaybillCustomsDocumentsCount(
    ctx: StateContext<ShipmentWaybillItemStateModel>,
    { payload }: ShipmentWaybillCustomsDocumentsCountLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.authFacade
      .hasPermission$(
        PERMISSION_CONFIG[Permission.TrackAndTrace][Permission.CanaryTransportLevel][Permission.ShipmentCustoms][
          Permission.Read
        ],
      )
      .pipe(
        switchMap((condition: boolean) => {
          if (!condition) {
            ctx.patchState({ documentsLoaded: true });

            return of(undefined);
          }

          return this.shipmentWaybillItemService.getShipmentWaybillCustomsDocumentsCount(payload).pipe(
            map((count: number) => {
              ctx.patchState({ waybillCustomsDocumentsCount: count, documentsLoaded: true });
            }),
            catchError(({ error }: CustomHttpErrorResponse<Error>) =>
              of(this.notificationService.error(error.message)),
            ),
          );
        }),
      );
  }

  @Action(ShipmentWaybillItemNotificationListCountLoad)
  getShipmentWaybillHasNotification(
    ctx: StateContext<ShipmentWaybillItemStateModel>,
    { payload }: ShipmentWaybillItemNotificationListCountLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillItemService.getNotificationListCount(payload).pipe(
      map((notificationListCount: number) => {
        ctx.patchState({ ...ctx.getState(), notificationListCount, notificationsLoaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmenWaybillDemurrageAndDetentionTabAvailability)
  getShipmentWaybillDemurrageAndDetentionTabAvailability(
    ctx: StateContext<ShipmentWaybillItemStateModel>,
    { payload }: ShipmenWaybillDemurrageAndDetentionTabAvailability,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillItemService.getShipmentWaybillDemurrageAndDetentionTabAvailability(payload).pipe(
      map((demurrageAndDetentionAvailable: boolean) => {
        ctx.patchState({ ...ctx.getState(), demurrageAndDetentionAvailable, dndLoaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillItemSetInitial)
  setInitialState(ctx: StateContext<ShipmentWaybillItemStateModel>): void {
    ctx.setState(initialState);
  }
}
